import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Contact from "@/components/Contact";
import { tilbehorCategories, TilbehorCategory } from "@/data/tilbehor";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import PageMeta from "@/components/PageMeta";

// Quick navigation categories
const quickNavCategories = [
  { id: "sko", label: "Sko" },
  { id: "strompebukser", label: "Strømpebukser" },
  { id: "harboyle", label: "Hårbøyler" },
  { id: "slips", label: "Slips" },
  { id: "paraply", label: "Paraplyer" },
  { id: "rengjoring", label: "Rengjøring" },
];

interface SelectedImage {
  src: string;
  category: TilbehorCategory;
}

const Tilbehor = () => {
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToCategory = (categoryId: string) => {
    const element = document.getElementById(categoryId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const handleImageClick = (image: string, category: TilbehorCategory) => {
    setSelectedImage({ src: image, category });
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Bunadstilbehør – Sko, slips, hårbøyler og mer"
        description="Komplett utvalg av bunadstilbehør fra Bunadstuggu. Sko, strømpebukser, hårbøyler, slips, paraplyer og rengjøringsprodukter til bunaden din."
        path="/tilbehor"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-24 pb-8 lg:pt-32 lg:pb-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-sans text-sm mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Tilbake til forsiden
            </Link>

            <div className="text-center max-w-3xl mx-auto">
              <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
                Komplett utstyr
              </p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
                Tilbehør
              </h1>
              <p className="text-muted-foreground font-sans leading-relaxed mb-8">
                Vi tilbyr et bredt utvalg av tilbehør for å gjøre bunaden din komplett. 
                Alt fra sko og hårbøyler til paraplyer og rengjøringsprodukter. 
                Kontakt oss hvis du skulle lure på noe.
              </p>

              {/* Category Quick Navigation */}
              <div className="flex flex-wrap justify-center gap-3">
                {quickNavCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => scrollToCategory(cat.id)}
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-full font-sans text-sm hover:bg-primary/90 transition-colors"
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Sections */}
        {tilbehorCategories.map((category, categoryIndex) => (
          <section 
            key={category.id} 
            id={category.id}
            className={`py-12 lg:py-16 ${categoryIndex % 2 === 0 ? 'bg-cream' : 'bg-background'}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <div className="text-center mb-10">
                <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-3">
                  {category.title}
                </h2>
                <p className="text-muted-foreground font-sans text-sm max-w-xl mx-auto">
                  {category.description}
                </p>
              </div>

              {/* Image grid with centered layout */}
              <div className="flex justify-center">
                <div className={`grid gap-4 md:gap-6 ${
                  category.images.length === 1 
                    ? 'grid-cols-1 max-w-md' 
                    : category.images.length === 2 
                    ? 'grid-cols-2 max-w-2xl' 
                    : category.images.length === 3 
                    ? 'grid-cols-3 max-w-4xl'
                    : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 w-full'
                }`}>
                  {category.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => handleImageClick(image, category)}
                      className="aspect-square overflow-hidden bg-cream cursor-pointer group rounded-sm shadow-soft"
                    >
                      <img
                        src={image}
                        alt={`${category.title} ${index + 1}`}
                        className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Contact Section */}
        <Contact />
      </main>

      <Footer />

      {/* Image Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl w-[90vw] p-0 bg-background border-none overflow-hidden">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-10 p-2 bg-background/80 rounded-full text-foreground hover:bg-background transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedImage && (
            <div className="flex flex-col">
              <img
                src={selectedImage.src}
                alt={selectedImage.category.modalTitle}
                className="w-full h-auto max-h-[75vh] object-contain"
              />
              <div className="p-4 bg-cream text-center">
                <p className="font-serif text-lg text-foreground">
                  {selectedImage.category.modalTitle}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tilbehor;
